import { useMemo, useRef } from "react";
import { Formik } from "formik";
import { Form, Button as BSButton, Col, FloatingLabel, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ErrorPage } from "../error";
import { defaultTenantFeatures, TenantFeaturesModel } from "../../data/entities";
import { PermissionDefinitions } from "../../security";
import { usePageActions } from "../";
import { TenantService } from "../../services";
import { getUserTenantId, setSuccess } from "../../app";
import { useAsync, usePermission, useUrlParams } from "../../hooks";
import { isValidGoogleTagManagerId, normalizeGoogleTagManagerId } from "../../utils";
import {
  Button,
  Icon,
  IconAndText,
  IconDefinitions,
  Card,
  SpinnerDefault,
} from "../../components";

type FeatureToggleName = {
  [K in keyof TenantFeaturesModel]: TenantFeaturesModel[K] extends boolean ? K : never;
}[keyof TenantFeaturesModel];

interface FeatureToggleDefinition {
  name: FeatureToggleName;
  label: string;
  description: string;
  isAvailable?: (values: TenantFeaturesModel) => boolean;
  requirementHint?: string;
}

const featureToggles: FeatureToggleDefinition[] = [
  {
    name: "manageProviders",
    label: "Provider Management",
    description: "Allow the tenant to add and remove providers, edit provider details, and change which services each provider offers. Profile pictures, availability, schedules, and service durations stay editable either way.",
  },
  {
    name: "manageServiceTypes",
    label: "Service Management",
    description: "Allow the tenant to edit service types and the services within each type.",
  },
  {
    name: "manageLocationServices",
    label: "Location Service Management",
    description: "Allow the tenant to modify which services are offered at each location.",
  },
  {
    name: "managePricing",
    label: "Pricing Management",
    description: "Allow the tenant to modify service pricing and charge configuration.",
    isAvailable: (values) => values.manageServiceTypes || values.manageLocationServices,
    requirementHint: "Requires \"Service Management\" or \"Location Service Management\" to be enabled.",
  },
  {
    name: "autoCopyCustomerNotes",
    label: "Auto-Copy Customer Notes",
    description: "When staff manually add an appointment and pick an existing customer, pre-fill the notes with the notes from that customer's most recent appointment. Staff can still edit or clear them before saving.",
  },
];

export function HostTenantFeaturesPage() {
  const tenantService = useMemo(() => new TenantService(), []);
  const id = parseInt(useUrlParams("id"));
  const submitRef = useRef<HTMLButtonElement>(null);
  const dispatch = useDispatch();
  const { value: features, loading, error } = useAsync(
    () => tenantService.getFeaturesAsync(id),
    [id]
  );

  const tenantId = useSelector(getUserTenantId());
  const canEditTenant = usePermission(PermissionDefinitions.host.edit(tenantId!));

  usePageActions(
    canEditTenant && (
      <Button onClick={() => submitRef.current?.click()}>
        <IconAndText iconName={IconDefinitions.save} text="Save" />
      </Button>
    ),
    [canEditTenant]
  );

  if (error) return <ErrorPage message={error.message} />
  if (loading) return <SpinnerDefault />

  return (
    <Card title="Features">
      <Formik
        initialValues={{ ...defaultTenantFeatures, ...(features ?? {}) }}
        validate={(values) => {
          const gtmId = normalizeGoogleTagManagerId(values.googleTagManagerId);
          return gtmId && !isValidGoogleTagManagerId(gtmId)
            ? { googleTagManagerId: "Enter a container ID like GTM-ABC1234, or leave blank." }
            : {};
        }}
        onSubmit={async (values) => {
          const normalized = {
            ...values,
            googleTagManagerId: normalizeGoogleTagManagerId(values.googleTagManagerId),
          };
          featureToggles.forEach((toggle) => {
            if (toggle.isAvailable && !toggle.isAvailable(values)) {
              normalized[toggle.name] = false;
            }
          });

          const saved = await tenantService.saveFeaturesAsync(id, normalized);
          if (saved) {
            dispatch(setSuccess("Tenant features saved successfully."));
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
          <Form onSubmit={handleSubmit} noValidate>
            {featureToggles.map((toggle) => {
              const available = toggle.isAvailable ? toggle.isAvailable(values) : true;
              return (
                <Row key={toggle.name} className="mb-3">
                  <Col>
                    <Form.Check
                      type="switch"
                      id={`tenant-feature-${toggle.name}`}
                      name={toggle.name}
                      label={toggle.label}
                      checked={values[toggle.name] && available}
                      onChange={handleChange}
                      disabled={!canEditTenant || isSubmitting || !available}
                    />
                    <Form.Text muted>{toggle.description}</Form.Text>
                    {!available && toggle.requirementHint && (
                      <div className="text-muted opacity-75 mt-1" style={{ fontSize: "0.75rem" }}>
                        <Icon iconName={IconDefinitions.infoCircle} className="me-1" />
                        {toggle.requirementHint}
                      </div>
                    )}
                  </Col>
                </Row>
              );
            })}
            <h5 className="mt-4 mb-3">Tracking</h5>
            <Row className="mb-3">
              <Col xs={24} md={12}>
                <Form.Group controlId="tenant-feature-googleTagManagerId">
                  <FloatingLabel label="Google Tag Manager Container ID">
                    <Form.Control
                      type="text"
                      name="googleTagManagerId"
                      placeholder="GTM-ABC1234"
                      value={values.googleTagManagerId ?? ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!canEditTenant || isSubmitting}
                      isInvalid={touched.googleTagManagerId && !!errors.googleTagManagerId}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.googleTagManagerId}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
                <Form.Text muted>
                  Loads this Google Tag Manager container on the tenant's public booking pages and sends booking
                  events (service selection, checkout and purchase conversions with SKUs) to its dataLayer. Leave
                  blank to disable tracking.
                </Form.Text>
              </Col>
            </Row>
            <BSButton ref={submitRef} type="submit" className="d-none" />
          </Form>
        )}
      </Formik>
    </Card>
  );
}

export default HostTenantFeaturesPage;
